<script setup lang="ts">
import type { HistoryItem } from '@/api/modules/customerServiceConversation'
import type { CustomerServiceTicket } from '@/api/modules/customerServiceTicket'
import type { ProTableColumn } from '@/components/FaProTable/types'
import { FaButton } from '@fantastic-admin/components'
import AppStatusTag from '@/components/AppDisplay/StatusTag.vue'
import FaProTable from '@/components/FaProTable/index.vue'
import { formatDateTime } from '../../utils/userDisplay'

defineOptions({ name: 'UserServiceTab' })

defineProps<{
  conversations: { list: HistoryItem[], total: number }
  tickets: { list: CustomerServiceTicket[], total: number }
  unavailable: boolean
  canViewConversations: boolean
  canViewTickets: boolean
}>()
const emit = defineEmits<{ navigate: [path: string, query: Record<string, number>] }>()

const conversationColumns: ProTableColumn<HistoryItem>[] = [
  { prop: 'conversation_no', label: '会话号', minWidth: 210 },
  { prop: 'status', label: '状态', width: 120, render: ({ row }) => h(AppStatusTag, { label: row.status || '-', tone: row.status === 'closed' ? 'default' : 'success' }) },
  { prop: 'reception.main_agent_id', label: '接待坐席', width: 120, formatter: row => row.reception?.main_agent_id ? `坐席 #${row.reception.main_agent_id}` : '-' },
  { prop: 'rating', label: '评价', width: 90, formatter: row => row.rating ? `${row.rating} 星` : '-' },
  { prop: 'last_message_at', label: '最后服务', width: 170, formatter: row => formatDateTime(row.last_message_at || row.created_at) },
]
const ticketColumns: ProTableColumn<CustomerServiceTicket>[] = [
  { prop: 'ticket_no', label: '工单号', minWidth: 200 },
  { prop: 'title', label: '工单主题', minWidth: 240 },
  { prop: 'priority', label: '优先级', width: 100 },
  { prop: 'status', label: '状态', width: 110 },
  { prop: 'created_at', label: '创建时间', width: 170, formatter: row => formatDateTime(row.created_at) },
]
</script>

<template>
  <div class="space-y-4">
    <div v-if="unavailable" class="border-warning/25 bg-warning/5 text-sm px-4 py-3 border rounded-3">
      当前账号无客服记录查看权限，或客服服务暂不可用。
    </div>
    <FaCard title="客服会话结论" content-class="pt-3">
      <div class="mb-3 flex flex-wrap gap-3 items-center justify-between">
        <span class="text-sm text-muted-foreground">共 {{ conversations.total }} 次会话</span><FaButton v-if="canViewConversations" variant="outline" size="sm" @click="emit('navigate', '/service/conversation/history', {})">
          进入会话记录
        </FaButton>
      </div>
      <FaProTable :columns="conversationColumns" :data="conversations.list" :show-toolbar="false" :show-pagination="false" :bordered="false" row-key="id" />
    </FaCard>
    <FaCard title="关联工单" content-class="pt-3">
      <div class="mb-3 flex flex-wrap gap-3 items-center justify-between">
        <span class="text-sm text-muted-foreground">共 {{ tickets.total }} 条工单</span><FaButton v-if="canViewTickets" variant="outline" size="sm" @click="emit('navigate', '/service/ticket/ticket', {})">
          进入工单列表
        </FaButton>
      </div>
      <FaProTable :columns="ticketColumns" :data="tickets.list" :show-toolbar="false" :show-pagination="false" :bordered="false" row-key="id" />
    </FaCard>
  </div>
</template>
